import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ── Global Axios Interceptors (Bearer token auth, no withCredentials) ──
// This runs once on module load and applies to ALL axios calls app-wide.
// It overrides any per-request withCredentials: true so cross-origin CORS works.

const TOKEN_KEY = 'lustlink_access_token';
const REFRESH_KEY = 'lustlink_refresh_token';

axios.interceptors.request.use(config => {
  // Force withCredentials off to avoid CORS credential mode conflicts
  config.withCredentials = false;
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (!refreshToken) return Promise.reject(error);

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(`${API}/api/auth/refresh`, {
          refresh_token: refreshToken,
        });
        const newToken = res.data.access_token;
        localStorage.setItem(TOKEN_KEY, newToken);
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// ── Auth Context ──

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const checkedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    // Safety timeout — never hang the splash forever.
    // If /api/auth/me doesn't resolve within 8s, treat as unauthenticated and let the app render.
    const timeoutId = setTimeout(() => {
      console.warn('[Auth] /api/auth/me timed out after 8s — proceeding as unauthenticated');
      setUser(null);
      setLoading(false);
    }, 8000);
    try {
      const response = await axios.get(`${API}/api/auth/me`, { timeout: 7500 });
      setUser(response.data);
    } catch (err) {
      // Token invalid/expired and refresh also failed
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      setUser(null);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!checkedRef.current) {
      checkedRef.current = true;
      checkAuth();
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/api/auth/login`, { email, password });
      // Store tokens from response body
      if (response.data.access_token) {
        localStorage.setItem(TOKEN_KEY, response.data.access_token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem(REFRESH_KEY, response.data.refresh_token);
      }
      const userData = { ...response.data };
      delete userData.access_token;
      delete userData.refresh_token;
      setUser(userData);
      return userData;
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail);
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const register = async (userData) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/api/auth/register`, userData);
      if (response.data.access_token) {
        localStorage.setItem(TOKEN_KEY, response.data.access_token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem(REFRESH_KEY, response.data.refresh_token);
      }
      const user = { ...response.data };
      delete user.access_token;
      delete user.refresh_token;
      setUser(user);
      return user;
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail);
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {});
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      setUser(null);
    }
  };

  const verifyAge = async (dateOfBirth, agreeToTerms) => {
    try {
      const response = await axios.post(`${API}/api/auth/verify-age`, {
        date_of_birth: dateOfBirth,
        agree_to_terms: agreeToTerms,
      });
      setUser(prev => ({ ...prev, is_age_verified: true }));
      return response.data;
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail);
      throw new Error(errorMsg);
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        verifyAge,
        updateUser,
        refreshUser,
        isAuthenticated: !!user,
        isAgeVerified: user?.is_age_verified || false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
